import React, { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import Wordmark from "@/components/Wordmark";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowRight,
  AtSign,
  BrainCircuit,
  Camera,
  Check,
  ChevronDown,
  GitBranch,
  Handshake,
  Hourglass,
  Image as ImageIcon,
  Menu,
  MessagesSquare,
  Sparkles,
  Unlink,
  Users,
  X,
} from "lucide-react";

const NAV_LINKS = [
  { href: "#features", label: "Features" },
  { href: "#how", label: "How it works" },
  { href: "#pricing", label: "Pricing" },
  { href: "#faq", label: "FAQ" },
];

const FEATURE_TILES = [
  {
    Icon: MessagesSquare,
    title: "Multiplayer chats",
    body: "Everyone in your workspace and Claude share one conversation. No more screenshotting AI replies into other tools.",
  },
  {
    Icon: AtSign,
    title: "Private side-channels",
    body: "Use @mentions or /private to talk just between humans. Claude never sees those messages.",
  },
  {
    Icon: BrainCircuit,
    title: "Project memory",
    body: "Claude remembers across every chat in your workspace, so you stop re-explaining context.",
  },
  {
    Icon: Sparkles,
    title: "Visual artifacts",
    body: "HTML and SVG render inline in a sandboxed preview. Iterate on a landing page right inside the thread.",
  },
  {
    Icon: ImageIcon,
    title: "Vision",
    body: "Paste a screenshot. Claude reads it, explains it, debugs it, answers questions about it.",
  },
  {
    Icon: Users,
    title: "Cross-team workspaces",
    body: "Invite anyone by email. Internal teams, clients, partners, freelancers — all in one space.",
  },
];

const HOW_STEPS = [
  {
    n: "01",
    title: "Create a workspace",
    body: "Sign up in 20 seconds. Name your workspace. Invite collaborators by email.",
  },
  {
    n: "02",
    title: "Chat together",
    body: "Everyone sees who said what — Maya, Jordan, Claude, all attributed. Side-channel privately with @mention.",
  },
  {
    n: "03",
    title: "Build with Claude",
    body: "Render HTML/SVG inline, paste screenshots for vision, flip on project memory so Claude remembers.",
  },
];

const COLLAB_SCENARIOS = [
  {
    Icon: Users,
    title: "Inside your team",
    body: "Marketing, design, and product think together with Claude on the same brief. Everyone sees who said what.",
  },
  {
    Icon: Handshake,
    title: "With your clients",
    body: "Agency + client in one space. Share work, get sign-off, no email threads, no separate Slack.",
  },
  {
    Icon: GitBranch,
    title: "Across partnerships",
    body: "Two startups, one shared workspace. Both founders, both teams, one Claude conversation.",
  },
];

const PRICING = [
  {
    plan: "Solo Free",
    price: "$0",
    cadence: "forever",
    blurb: "Try it out solo or with one collaborator.",
    bullets: [
      "$3 of included Claude usage",
      "1 person",
      "All features",
    ],
    cta: "Get started",
    ctaHref: "/signup",
    accent: false,
  },
  {
    plan: "Team",
    price: "$19.99",
    cadence: "per workspace, per month",
    blurb: "For small groups that already share a project.",
    bullets: [
      "$5 of Claude usage every month",
      "Up to 2 teammates",
      "All features",
    ],
    cta: "Start Team",
    ctaHref: "/signup?plan=team",
    accent: true,
  },
  {
    plan: "Team Plus",
    price: "$29.99",
    cadence: "per workspace, per month",
    blurb: "Heavier usage, larger projects, vision-heavy work.",
    bullets: [
      "$10 of Claude usage every month",
      "Up to 10 teammates",
      "All features",
    ],
    cta: "Start Team Plus",
    ctaHref: "/signup?plan=team_plus",
    accent: false,
  },
];

const FAQ = [
  {
    q: "How does usage work?",
    a: "Solo Free includes $3 of Claude usage to get started. Team includes $5 of Claude usage refreshed every month; Team Plus includes $10. Need more? Add prepaid credits any time.",
  },
  {
    q: "What does the $3 trial cover?",
    a: "Your $3 trial covers Claude usage for several thousand short messages. After that, upgrade to a Team plan for ongoing use.",
  },
  {
    q: "Can I invite people from outside my company?",
    a: "Yes. onebrain workspaces aren't tied to an organization — invite anyone by email, regardless of company or domain. There's no separate \u201Cguest\u201D tier and no cross-org friction. The owner of the workspace handles billing; everyone in it just chats.",
  },
  {
    q: "Is my conversation data private?",
    a: "Yes. We don't train any AI model on your messages, and onebrain staff don't read them except where strictly necessary to investigate an incident you've reported. Anthropic processes the messages only to generate Claude's replies. See our Privacy Policy.",
  },
  {
    q: "What happens when my trial runs out?",
    a: "Claude stops replying until you upgrade. Your chats stay readable forever. You can also top up a paid workspace with a one-time amount, like a prepaid SIM.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Anytime, from the billing tab. You keep access until the end of the period you've already paid for, then the workspace returns to the free tier.",
  },
];

const FRICTION_TILES = [
  {
    Icon: Camera,
    title: "Screenshots flying around",
    body: "Your best AI conversation is trapped in a chat window only you can see. Sharing it means screenshots, paste-jobs, or rewriting the whole thing.",
  },
  {
    Icon: Unlink,
    title: "Context gets lost",
    body: "By the time your teammate joins, half the back-and-forth has been summarized in Slack. The model doesn't remember what you were both thinking.",
  },
  {
    Icon: Hourglass,
    title: "Ideation goes choppy",
    body: "Two people building on the same idea can't both be in the same AI conversation. You take turns. The momentum dies.",
  },
];

export default function LandingPage() {
  const { user } = useAuth();
  if (user && user.id) {
    return <Navigate to="/" replace />;
  }
  return (
    <div className="min-h-screen bg-[#FAF9F7] text-[#2A2D34] font-sans antialiased">
      <Nav />
      <Hero />
      <Problem />
      <HowItWorks />
      <Collaboration />
      <Features />
      <Pricing />
      <FaqList />
      <Footer />
    </div>
  );
}

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`sticky top-0 z-40 backdrop-blur transition-all ${
        scrolled
          ? "bg-[#FAF9F7]/85 border-b border-[#ECE7DC]"
          : "bg-[#FAF9F7]/60 border-b border-transparent"
      }`}
      data-testid="landing-navbar"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-2.5"
          data-testid="nav-home-link"
        >
          <BrainMarkSmall />
          <Wordmark size="md" testid="nav-wordmark" />
        </Link>

        <nav
          className="hidden md:flex items-center gap-7"
          data-testid="nav-desktop"
        >
          {NAV_LINKS.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="font-sans text-sm text-[#5E5C58] hover:text-[#2A2D34] transition-colors"
              data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2.5">
          <Link
            to="/login"
            className="font-sans text-sm text-[#2A2D34] px-3.5 py-2 rounded-lg hover:bg-[#F1ECDF] transition-colors"
            data-testid="nav-signin-link"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            className="font-sans text-sm font-medium text-white bg-[#1E7BFF] hover:bg-[#155EC9] px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5"
            data-testid="nav-cta-button"
          >
            Create workspace
            <ArrowRight size={14} />
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden p-2 -mr-2 text-[#2A2D34]"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          data-testid="nav-mobile-toggle"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileOpen ? (
        <div
          className="md:hidden border-t border-[#ECE7DC] bg-[#FAF9F7]"
          data-testid="nav-mobile-menu"
        >
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV_LINKS.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setMobileOpen(false)}
                className="font-sans text-sm text-[#3F4148] py-2.5"
              >
                {n.label}
              </a>
            ))}
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="font-sans text-sm text-[#2A2D34] border border-[#ECE7DC] py-2.5 rounded-lg text-center"
              >
                Sign in
              </Link>
              <Link
                to="/signup"
                onClick={() => setMobileOpen(false)}
                className="font-sans text-sm font-medium text-white bg-[#1E7BFF] py-2.5 rounded-lg text-center"
              >
                Create workspace
              </Link>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}

function BrainMarkSmall() {
  return (
    <img
      src="/brand/onebrain-mark-192.png"
      alt=""
      aria-hidden="true"
      className="w-7 h-7 rounded-md"
      data-testid="brain-mark-small"
    />
  );
}

function Hero() {
  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-20 sm:pb-24"
      data-testid="landing-hero"
    >
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <div className="lg:col-span-7">
          <p
            className="font-sans text-[11px] uppercase tracking-[0.22em] text-[#1E7BFF] font-semibold"
            data-testid="hero-eyebrow"
          >
            Multiplayer Claude for small teams
          </p>
          <h1
            className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#2A2D34] mt-5"
            data-testid="hero-headline"
          >
            Think together
            <br />
            <span className="text-[#1E7BFF]">with Claude.</span>
          </h1>
          <p
            className="font-sans text-base sm:text-lg text-[#3F4148] mt-6 leading-relaxed max-w-[60ch]"
            data-testid="hero-subheadline"
          >
            A shared workspace where humans and Claude chat side-by-side.
            Invite anyone — your team, another team, your client — and work in
            the same conversation.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 bg-[#1E7BFF] hover:bg-[#155EC9] text-white font-sans font-medium text-sm px-5 py-3 rounded-lg transition-colors w-full sm:w-auto"
              data-testid="hero-cta-primary"
            >
              Create your workspace free
              <ArrowRight size={15} />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-[#2A2D34] font-sans font-medium text-sm px-5 py-3 rounded-lg border border-[#DCD7CE] hover:bg-[#F1ECDF] transition-colors w-full sm:w-auto"
              data-testid="hero-cta-secondary"
            >
              Sign in
            </Link>
          </div>
          <p className="font-sans text-xs text-[#7A7571] mt-4">
            Solo Free includes $3 of Claude credit. No card required.
          </p>
        </div>

        <div className="lg:col-span-5">
          <ChatMockup />
        </div>
      </div>
    </section>
  );
}

function ChatMockup() {
  return (
    <div
      className="relative rounded-2xl bg-white border border-[#ECE7DC] shadow-[0_8px_30px_rgba(30,123,255,0.08)] p-5 sm:p-6 max-w-md mx-auto lg:max-w-none"
      data-testid="hero-mockup"
    >
      <div className="flex items-center gap-2 pb-4 border-b border-[#ECE7DC] mb-4">
        <span className="w-2.5 h-2.5 rounded-full bg-[#F0B23A]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#D96C4A]" />
        <span className="w-2.5 h-2.5 rounded-full bg-[#5B9270]" />
        <span className="ml-2 font-mono text-[11px] text-[#A5A29E]">
          launch-plan
        </span>
      </div>
      <Bubble who="Maya" tone="terracotta" align="right">
        Claude, sketch a hero with our blue accent — keep it editorial.
      </Bubble>
      <Bubble who="Claude Opus 4.8" tone="claude" align="left">
        On it. Charcoal headline, blue accent on the verb, generous spacing.
        Opening an HTML artifact below.
        <ArtifactChip />
      </Bubble>
      <Bubble who="Jordan" tone="moss" align="left">
        Bump the blue down to <strong>#1E7BFF</strong> exactly, and tighten the
        leading.
      </Bubble>
      <Bubble who="Maya" tone="terracotta" align="right" small>
        @Jordan — keep the brain mark next to the wordmark on mobile too
      </Bubble>
    </div>
  );
}

function Bubble({ who, tone, align, children, small }) {
  const isRight = align === "right";
  const palette = {
    terracotta: { bg: "#FAE7DC", fg: "#7A4A2C", name: "#7A4A2C" },
    moss: { bg: "#E4ECDC", fg: "#3A5A40", name: "#3A5A40" },
    claude: { bg: "#F4F8FF", fg: "#2A2D34", name: "#1E7BFF" },
  }[tone];
  return (
    <div className={`mb-3 flex ${isRight ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] ${
          isRight ? "items-end" : "items-start"
        } flex flex-col`}
      >
        <span
          className="font-sans text-[10px] uppercase tracking-[0.14em] font-semibold mb-1"
          style={{ color: palette.name }}
        >
          {who}
        </span>
        <div
          className={`rounded-2xl px-3.5 py-2.5 font-sans ${
            small ? "text-xs" : "text-sm"
          } leading-snug`}
          style={{
            background: palette.bg,
            color: palette.fg,
            borderTopRightRadius: isRight ? 4 : undefined,
            borderTopLeftRadius: !isRight ? 4 : undefined,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function ArtifactChip() {
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white border border-[#DCE4F4] text-[#1E7BFF] text-[11px] font-mono">
      <Sparkles size={11} />
      hero.html · preview
    </div>
  );
}

function Problem() {
  return (
    <section
      className="bg-[#F4F1EA] border-y border-[#ECE7DC]"
      data-testid="landing-problem"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <p
          className="font-sans text-[11px] uppercase tracking-[0.22em] text-[#1E7BFF] font-semibold"
          data-testid="problem-eyebrow"
        >
          The problem
        </p>
        <h2
          className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#2A2D34] mt-5 max-w-3xl"
          data-testid="problem-headline"
        >
          AI conversations were built for one person at a time.
        </h2>
        <p
          className="font-sans text-base sm:text-lg text-[#3F4148] mt-6 max-w-3xl leading-relaxed"
          data-testid="problem-body"
        >
          So every time you want to bring a teammate in, you screenshot the
          chat, paste it into Slack, re-explain what you were trying to
          do, and watch the thread fragment. The AI loses context. Your
          teammate is half-in, half-out. Real ideation gets stuck in
          copy-paste.
        </p>

        <div className="grid sm:grid-cols-3 gap-5 sm:gap-7 mt-14">
          {FRICTION_TILES.map((t, i) => (
            <div
              key={t.title}
              className="rounded-xl border border-[#E2DCCF] bg-transparent p-5 sm:p-6"
              data-testid={`problem-tile-${i}`}
            >
              <div className="w-9 h-9 rounded-md bg-[#E8E3D7] text-[#6B6862] flex items-center justify-center mb-4">
                <t.Icon size={16} strokeWidth={1.8} />
              </div>
              <h3 className="font-serif text-base sm:text-lg text-[#2A2D34]">
                {t.title}
              </h3>
              <p className="font-sans text-sm text-[#5E5C58] mt-1.5 leading-relaxed">
                {t.body}
              </p>
            </div>
          ))}
        </div>

        <p
          className="font-serif italic text-base sm:text-lg text-[#5E5C58] mt-14 max-w-2xl leading-relaxed"
          data-testid="problem-closing"
        >
          onebrain puts everyone in the same conversation — including Claude.
          No more screenshots. No more re-explaining. No more taking turns.
        </p>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section
      id="how"
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
      data-testid="landing-how"
    >
      <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2D34] mb-3">
        How it works
      </h2>
      <p className="font-sans text-base text-[#3F4148] max-w-2xl mb-12">
        Three steps. No tutorial videos. No onboarding maze.
      </p>
      <div className="grid sm:grid-cols-3 gap-6 sm:gap-8">
        {HOW_STEPS.map((s) => (
          <div
            key={s.n}
            className="rounded-xl bg-white border border-[#ECE7DC] p-6 hover:border-[#1E7BFF]/30 transition-colors"
            data-testid={`how-step-${s.n}`}
          >
            <p className="font-mono text-xs text-[#1E7BFF]">{s.n}</p>
            <h3 className="font-serif text-xl text-[#2A2D34] mt-2">
              {s.title}
            </h3>
            <p className="font-sans text-sm text-[#3F4148] mt-2 leading-relaxed">
              {s.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Collaboration() {
  return (
    <section
      className="bg-[#F4F0E6] border-y border-[#ECE7DC]"
      data-testid="landing-collaboration"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2D34] max-w-3xl">
          Built for teams that don&rsquo;t fit in one company
        </h2>
        <p className="font-sans text-base text-[#3F4148] mt-4 max-w-2xl leading-relaxed">
          Most chat tools assume everyone&rsquo;s on the same payroll. onebrain
          doesn&rsquo;t. Invite anyone by email — your team, another team, your
          client, a partner — and you all share the same workspace.
        </p>
        <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mt-12">
          {COLLAB_SCENARIOS.map((s, i) => (
            <div
              key={s.title}
              className="rounded-xl bg-white border border-[#ECE7DC] p-6"
              data-testid={`collab-scenario-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#E8F0FF] text-[#1E7BFF] flex items-center justify-center mb-4">
                <s.Icon size={18} strokeWidth={1.8} />
              </div>
              <h3 className="font-serif text-lg text-[#2A2D34]">{s.title}</h3>
              <p className="font-sans text-sm text-[#3F4148] mt-1.5 leading-relaxed">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section
      id="features"
      className="bg-white border-y border-[#ECE7DC]"
      data-testid="landing-features"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2D34] mb-3">
          Built for actual teamwork.
        </h2>
        <p className="font-sans text-base text-[#3F4148] max-w-2xl mb-12">
          Not a chatbot wrapper. The features your group needs to think out
          loud, together.
        </p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {FEATURE_TILES.map((f, i) => (
            <div
              key={f.title}
              className="rounded-xl bg-[#FAF9F7] border border-[#ECE7DC] p-5 sm:p-6"
              data-testid={`feature-tile-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-[#E8F0FF] text-[#1E7BFF] flex items-center justify-center mb-4">
                <f.Icon size={18} strokeWidth={1.8} />
              </div>
              <h3 className="font-serif text-lg text-[#2A2D34]">{f.title}</h3>
              <p className="font-sans text-sm text-[#3F4148] mt-1.5 leading-relaxed">
                {f.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section
      id="pricing"
      className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24"
      data-testid="landing-pricing"
    >
      <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2D34] mb-3">
        Simple pricing.
      </h2>
      <p className="font-sans text-base text-[#3F4148] max-w-2xl mb-12">
        Pick a plan that fits the workspace. Pay-as-you-go on Claude usage when
        you cross the included trial.
      </p>
      <div className="grid sm:grid-cols-3 gap-5 sm:gap-6">
        {PRICING.map((p) => (
          <div
            key={p.plan}
            className={`rounded-2xl p-6 sm:p-7 border ${
              p.accent
                ? "bg-[#0D1B2A] border-[#1E7BFF] text-white shadow-[0_12px_40px_rgba(30,123,255,0.18)]"
                : "bg-white border-[#ECE7DC] text-[#2A2D34]"
            } flex flex-col`}
            data-testid={`pricing-card-${p.plan.toLowerCase().replace(/\s/g, "-")}`}
          >
            <p
              className={`font-mono text-xs uppercase tracking-[0.18em] ${
                p.accent ? "text-[#1E7BFF]" : "text-[#7A7571]"
              }`}
            >
              {p.plan}
            </p>
            <div className="mt-4 flex items-baseline gap-1.5">
              <span className="font-serif text-4xl sm:text-5xl">
                {p.price}
              </span>
              <span
                className={`font-sans text-xs ${
                  p.accent ? "text-[#A5BFE0]" : "text-[#7A7571]"
                }`}
              >
                {p.cadence}
              </span>
            </div>
            <p
              className={`font-sans text-sm mt-3 ${
                p.accent ? "text-[#D2DCEC]" : "text-[#3F4148]"
              }`}
            >
              {p.blurb}
            </p>
            <ul className="mt-6 space-y-2.5 mb-6">
              {p.bullets.map((b) => (
                <li
                  key={b}
                  className={`flex items-start gap-2 font-sans text-sm ${
                    p.accent ? "text-[#E8EDF5]" : "text-[#2A2D34]"
                  }`}
                >
                  <Check
                    size={14}
                    className="mt-0.5 text-[#1E7BFF]"
                    strokeWidth={2.5}
                  />
                  {b}
                </li>
              ))}
            </ul>
            <div className="mt-auto">
              <Link
                to={p.ctaHref}
                className={`block w-full text-center font-sans font-medium text-sm px-4 py-2.5 rounded-lg transition-colors ${
                  p.accent
                    ? "bg-[#1E7BFF] hover:bg-[#155EC9] text-white"
                    : "bg-[#2A2D34] hover:bg-[#1A1918] text-white"
                }`}
                data-testid={`pricing-cta-${p.plan.toLowerCase().replace(/\s/g, "-")}`}
              >
                {p.cta}
              </Link>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FaqList() {
  return (
    <section
      id="faq"
      className="bg-white border-t border-[#ECE7DC]"
      data-testid="landing-faq"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2D34] mb-10 text-center">
          Common questions
        </h2>
        <ul className="divide-y divide-[#ECE7DC] border-y border-[#ECE7DC]">
          {FAQ.map((item, i) => (
            <FaqItem key={item.q} item={item} idx={i} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function FaqItem({ item, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left"
        aria-expanded={open}
        data-testid={`faq-toggle-${idx}`}
      >
        <span className="font-serif text-lg text-[#2A2D34]">{item.q}</span>
        <ChevronDown
          size={18}
          className={`mt-1 text-[#7A7571] transition-transform shrink-0 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open ? (
        <p
          className="font-sans text-sm text-[#3F4148] leading-relaxed pb-5 pr-7"
          data-testid={`faq-answer-${idx}`}
        >
          {item.a}
        </p>
      ) : null}
    </li>
  );
}

function Footer() {
  return (
    <footer
      className="bg-[#FAF9F7] border-t border-[#ECE7DC]"
      data-testid="landing-footer"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <BrainMarkSmall />
            <Wordmark size="md" testid="footer-wordmark" />
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 font-sans text-sm text-[#7A7571]">
            <Link to="/legal/terms" className="hover:text-[#2A2D34]">
              Terms
            </Link>
            <Link to="/legal/privacy" className="hover:text-[#2A2D34]">
              Privacy
            </Link>
            <Link to="/login" className="hover:text-[#2A2D34]">
              Sign in
            </Link>
            <a
              href="mailto:info@onebrain.team"
              className="hover:text-[#2A2D34]"
            >
              info@onebrain.team
            </a>
          </div>
        </div>
        <div className="mt-6 pt-6 border-t border-[#ECE7DC]">
          <p className="font-sans text-xs text-[#A5A29E]">
            © onebrain (Sonrie LLC). Where your team thinks together.
          </p>
        </div>
      </div>
    </footer>
  );
}
